import { useState, useEffect, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import Grid from '../../components/grid/Grid';
import { getAllRoles, getAllUsers, updateUserRole, getAllfeatures, getRolefeatures, updateRoleFeatures } from '../../services/UserAuthorization.js';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import './UserManagement.css';

const UserManagement = () => {
    const [activeTab, setActiveTab] = useState('Users');
    const [roles, setRoles] = useState([]);
    const [features, setFeatures] = useState([]);
    const [roleFeatures, setRoleFeatures] = useState([]);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);

    const userHeaders = [
        { key: 'username', label: 'Username', width: '30%' },
        { key: 'email', label: 'Email', width: '40%' },
        { key: 'role', label: 'Role', width: '30%' }
    ];

    const roleHeaders = [
        { key: 'roleName', label: 'Role', width: '20%' },
        { key: 'features', label: 'Features', width: '80%' }
    ];

    const roleMap = useMemo(() => {
        const map = {};
        roles.forEach(r => map[r.role_id] = r.role_name);
        return map;
    }, [roles]);

    const featureMap = useMemo(() => {
        const map = {};
        features.forEach(f => map[f.feature_id] = f.feature_name);
        return map;
    }, [features]);

    useEffect(() => { loadInitialData(); }, []);
    useEffect(() => { buildTableData(); }, [activeTab, roles, features, roleFeatures]);
    useEffect(() => { setSelectedRows([]); }, [activeTab]);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            const [roleRes, featureRes, roleFeatureRes] = await Promise.all([
                getAllRoles(),
                getAllfeatures(),
                getRolefeatures()
            ]);
            setRoles(roleRes);
            setFeatures(featureRes);
            setRoleFeatures(roleFeatureRes);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const buildTableData = async () => {
        try {
            setLoading(true);
            if (activeTab === 'Users') {
                const users = await getAllUsers();
                const formattedUsers = users.map((u, i) => ({
                    id: i + 1,
                    username: u.user_id,
                    email: `${u.user_id}@quidelortho.com`,
                    role: roleMap[u.role_id] || '—'
                }));
                setData(formattedUsers);
                if (selectedRows.length) {
                    const id = selectedRows[0].id;
                    const row = formattedUsers.find(r => r.id === id);
                    if (row) setSelectedRows([row]);
                }
            } else {
                const formattedRoles = roles.map((role, i) => {
                    const rf = roleFeatures.find(r => r.role_id === role.role_id);
                    let featureNames = [];
                    if (rf?.feature_id) {
                        featureNames = rf.feature_id.split(',').map(id => featureMap[id]).filter(Boolean);
                    }
                    return {
                        id: i + 1,
                        roleId: role.role_id,
                        roleName: role.role_name,
                        features: featureNames.join(', ')
                    };
                });
                setData(formattedRoles);
                if (selectedRows.length) {
                    const id = selectedRows[0].id;
                    const row = formattedRoles.find(r => r.id === id);
                    if (row) setSelectedRows([row]);
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const refreshRoleFeatures = async () => {
        try {
            setLoading(true);
            const res = await getRolefeatures();
            setRoleFeatures(res);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const selectedItem = selectedRows.length ? selectedRows[0] : null;

    return (
        <div className="um-wrapper">
            <div className="um-header"><h4>User Management</h4></div>
            <div className="filter-button-row">
                <div className="btn-group">
                    {["Users", "Roles"].map(tab => (
                        <button key={tab} type="button" className={`btn ${activeTab === tab ? "btn-primary" : "btn-outline"} btn-action`} onClick={() => setActiveTab(tab)}>
                            {tab}
                        </button>
                    ))}
                </div>
            </div>
            <div className="um-content">
                <div className="um-grid">
                    <Grid
                        headers={activeTab === 'Users' ? userHeaders : roleHeaders}
                        data={data}
                        rowsPerPage={50}
                        canMultiSelect={false}
                        onRowClick={row => setSelectedRows([row])}
                        loading={loading}
                        sortable
                    />
                </div>
                <div className="um-details">
                    {!selectedItem && <div className="um-empty">Select a row to see details</div>}
                    {selectedItem && activeTab === 'Users' && <UserDetails user={selectedItem} onRoleUpdated={buildTableData} />}
                    {selectedItem && activeTab === 'Roles' && <RoleDetails role={selectedItem} onFeaturesUpdate={refreshRoleFeatures} />}
                </div>
            </div>
        </div>
    );
};

const UserDetails = ({ user, onRoleUpdated }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [selectedRole, setSelectedRole] = useState('');
    const [roles, setRoles] = useState([]);

    useEffect(() => {
        const loadRoles = async () => {
            try {
                const res = await getAllRoles();
                setRoles(res);
            } catch (err) {
                console.error(err);
            }
        };
        loadRoles();
    }, []);

    useEffect(() => {
        if (user) setSelectedRole(user.role);
    }, [user]);

    const handleSave = async () => {
        try {
            const selectedRoleObj = roles.find(r => r.role_name === selectedRole);
            if (!selectedRoleObj) {
                toast.error('Invalid role selected');
                return;
            }
            await updateUserRole({ role_id: selectedRoleObj.role_id, user_id: user.username });
            toast.success('Role updated successfully');
            setIsEditing(false);
            onRoleUpdated();
        } catch (err) {
            console.error(err);
            toast.error('Update failed');
        }
    };

    return (
        <div className="details-box">
            <h5>User Details</h5>
            <div className="detail-row"><label>Username</label><span>{user.username}</span></div>
            <div className="detail-row"><label>Email</label><span>{user.email}</span></div>
            <div className="detail-row">
                <label>Role</label>
                {!isEditing && <span>{user.role}</span>}
                {isEditing && (
                    <select className="form-select" value={selectedRole} onChange={e => setSelectedRole(e.target.value)}>
                        <option value="">Select Role</option>
                        {roles.map(r => <option key={r.role_id} value={r.role_name}>{r.role_name}</option>)}
                    </select>
                )}
            </div>
            <div className="detail-actions">
                {!isEditing && <Button size="sm" variant="primary" onClick={() => setIsEditing(true)}><FontAwesomeIcon icon={faEdit} /> Edit</Button>}
                {isEditing && (
                    <>
                        <Button size="sm" variant="primary" onClick={handleSave} disabled={!selectedRole || selectedRole === user.role}>Save</Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => setIsEditing(false)}>Cancel</Button>
                    </>
                )}
            </div>
        </div>
    );
};

const RoleDetails = ({ role, onFeaturesUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [allFeatures, setAllFeatures] = useState([]);
    const [selectedFeature, setSelectedFeature] = useState('');
    const [featureList, setFeatureList] = useState([]);

    useEffect(() => {
        const loadFeatures = async () => {
            try {
                const res = await getAllfeatures();
                setAllFeatures(res);
            } catch (err) {
                console.error(err);
            }
        };
        loadFeatures();
    }, []);

    useEffect(() => {
        if (role?.features) {
            setFeatureList(role.features.split(',').map(f => f.trim()).filter(Boolean));
        }
    }, [role]);

    const handleAdd = () => {
        if (!selectedFeature) return;
        if (featureList.includes(selectedFeature)) {
            toast.warning('Feature already added');
            return;
        }
        setFeatureList(p => [...p, selectedFeature]);
        setSelectedFeature('');
    };

    const handleRemove = index => {
        setFeatureList(p => p.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        try {
            if (!role?.roleId) {
                toast.error('Invalid role');
                return;
            }
            const featureIds = allFeatures.filter(f => featureList.includes(f.feature_name)).map(f => f.feature_id);
            if (!featureIds.length) {
                toast.error('Select at least one feature');
                return;
            }
            await updateRoleFeatures({ role_id: role.roleId, feature_id: featureIds.join(',') });
            toast.success('Features updated successfully');
            setIsEditing(false);
            if (onFeaturesUpdate) await onFeaturesUpdate();
        } catch (err) {
            console.error(err);
            toast.error('Update failed');
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        if (role?.features) {
            setFeatureList(role.features.split(',').map(f => f.trim()));
        }
    };

    return (
        <div className="details-box">
            <h5>Role Details</h5>
            <div className="detail-row"><label>Role</label><span>{role.roleName}</span></div>
            {!isEditing && (
                <>
                    <div className="detail-row"><label>Features</label><span>{role.features || '—'}</span></div>
                    <div className="detail-actions">
                        <Button size="sm" variant="primary" onClick={() => setIsEditing(true)}><FontAwesomeIcon icon={faEdit} /> Edit</Button>
                    </div>
                </>
            )}
            {isEditing && (
                <div className="d-flex flex-column gap-3 mt-2">
                    <div className="d-flex align-items-center gap-2">
                        <select className="form-select" value={selectedFeature} onChange={e => setSelectedFeature(e.target.value)} disabled={allFeatures.filter(f => !featureList.includes(f.feature_name)).length === 0}>
                            <option value="">Select Feature</option>
                            {allFeatures
                                .filter(f => !featureList.includes(f.feature_name))
                                .map(f => (
                                    <option key={f.feature_id} value={f.feature_name}>
                                        {f.feature_name}
                                    </option>
                                ))
                            }
                        </select>
                        <button type="button" className="btn-download" onClick={handleAdd} disabled={!selectedFeature}><FontAwesomeIcon icon={faPlus} /></button>
                    </div>
                    <div>
                        <div className="fw-semibold mb-1">Added Features ({featureList.length})</div>
                        <div className="added-deps-box">
                            {featureList.length === 0 ? (
                                <div className="text-muted small">No features added</div>
                            ) : (
                                <ul className="mb-0 ps-0">
                                    {featureList.map((f, idx) => (
                                        <li key={`${f}-${idx}`} className="d-flex align-items-center gap-2">
                                            <span className="flex-grow-1"><code>{f}</code></span>
                                            <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={() => handleRemove(idx)}><FontAwesomeIcon icon={faXmark} /></button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    <div className="d-flex justify-content-end gap-2 mt-2">
                        <Button size="sm" variant="outline-secondary" onClick={handleCancel}>Cancel</Button>
                        <Button size="sm" variant="primary" onClick={handleSave} disabled={featureList.length === 0}>Save</Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;